import { isValidElement, useContext } from 'react';

import { styled, Typography } from '@mui/material';
import MuiListItemText, {
  ListItemTextProps as MuiListItemTextProps,
} from '@mui/material/ListItemText';

import { ListContext } from './List';
import { tokens } from '../../theme/generated/tokens';
import { WithRef } from '../../types';

export type ListItemTextProps = MuiListItemTextProps;

const ListItemTextRoot = styled(MuiListItemText, {
  name: 'DsListItemText',
  slot: 'Root',
  target: 'DsListItemText-root',
  overridesResolver: (_, theme) => {
    return [theme.root];
  },
})<ListItemTextProps>({});

export const ListItemText = ({
  ref,
  ...props
}: WithRef<HTMLDivElement, ListItemTextProps>) => {
  const listContext = useContext(ListContext);

  const {
    children,
    disableTypography = false,
    primary: primaryProp,
    primaryTypographyProps,
    secondary: secondaryProp,
    secondaryTypographyProps,
    ...other
  } = props;

  const size = listContext?.size ?? 'medium';
  const primary = primaryProp != null ? primaryProp : children;
  const secondary = secondaryProp || null;

  let computedPrimaryTypographyProps: typeof primaryTypographyProps = {
    color: 'inherit',
  };
  if (primary != null && !disableTypography) {
    const isCustomTypography =
      isValidElement(primary) && primary.type === Typography;

    if (!isCustomTypography) {
      if (size === 'large') {
        computedPrimaryTypographyProps = {
          ...computedPrimaryTypographyProps,
          variant: secondary ? 'P14M' : 'P14R',
        };
      } else if (size === 'medium') {
        computedPrimaryTypographyProps = {
          ...computedPrimaryTypographyProps,
          variant: secondary ? 'P12M' : 'P14R',
        };
      } else {
        computedPrimaryTypographyProps = {
          ...computedPrimaryTypographyProps,
          variant: secondary ? 'P12M' : 'P12R',
        };
      }
    }
  }

  let computedSecondaryTypographyProps: typeof secondaryTypographyProps = {};
  if (secondary != null && !disableTypography) {
    const isCustomTypography =
      isValidElement(secondary) && secondary.type === Typography;

    if (!isCustomTypography) {
      switch (size) {
        case 'large':
          computedSecondaryTypographyProps = {
            variant: 'P12R',
          };
          break;
        case 'medium':
          computedSecondaryTypographyProps = {
            variant: 'P10R',
          };
          break;
        case 'small':
          computedSecondaryTypographyProps = {
            variant: 'P10R',
            lineHeight: '14px',
          };
          break;
      }

      computedSecondaryTypographyProps.color =
        tokens.theme.fg.structural.secondary;
    }
  }

  return (
    <ListItemTextRoot
      ref={ref}
      {...other}
      primary={primary}
      primaryTypographyProps={{
        ...computedPrimaryTypographyProps,
        ...primaryTypographyProps,
      }}
      secondary={secondary}
      secondaryTypographyProps={{
        ...computedSecondaryTypographyProps,
        ...secondaryTypographyProps,
      }}
    />
  );
};

ListItemText.displayName = 'ListItemText';
